import type { CommonSort, NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'
import { generatePath } from 'react-router-dom'

import { Pagination } from 'rsuite'
import ProductGroupsTable from './grid/ProductGroupsTable'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { SORT_ORDER_DESC, SORT_ORDER_ASC } from '@@partner:config/graphql'
import { ROUTE_CATALOG_CATEGORY_EDIT, ROUTE_PRODUCT_GROUPS_EDIT } from '@@partner:config/routes'
import ProductGroupsFilter from './grid/ProductGroupsFilter'
import { useFetchAllProductGroups } from '@@partner:graphql/query/productGroup/allProductGroups.gql'
import { convertToInt } from '@@partner:helpers/util'

export interface ProductGroupGridProps {
  brandSlug: string
}

export default function ProductGroupGrid ({ brandSlug }: ProductGroupGridProps) {
  const [ page, setPage ] = useState(1)
  const [ tableSort, setTableSort ] = useState<CommonSort>({
    fieldName: 'id',
    direction: SORT_ORDER_DESC
  })
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const { data, loading, error } = useFetchAllProductGroups({
    pager: { page },
    sort: tableSort,
    filter
  })

  const generateEditUrl = useCallback(
    (groupId) => {
      if (!brandSlug) {
        return ''
      }
      return generatePath(ROUTE_PRODUCT_GROUPS_EDIT, {
        brandSlug,
        groupId
      })
    },
    [ brandSlug ]
  )

  const handlePageSelect = useCallback((selectedPage: string | number) => {
    setPage(convertToInt(selectedPage))
  }, [])

  const handleSortColumn = useCallback((fieldName, direction) => {
    setTableSort({
      fieldName,
      direction: direction === 'desc' ? SORT_ORDER_DESC : SORT_ORDER_ASC
    })
  }, [])

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки групп товарв"
        error={error}
      />
    )
  }

  const pageInfo = data?.allProductGroups.pageInfo

  return (
    <div>
      <ProductGroupsFilter onFilterChanged={handleFilterChanged} />

      <ProductGroupsTable
        productGroups={data?.allProductGroups?.edges ?? []}
        loading={loading}
        tableSort={tableSort}
        onSortColumn={handleSortColumn}
        generateEditUrl={generateEditUrl}
      />

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={pageInfo?.lastPage}
        maxButtons={10}
        onSelect={handlePageSelect}
        total={pageInfo?.total ?? 0}
      ></Pagination>
    </div>
  )
}
